import { createSelector } from 'reselect';
import { AnalogInput, AnalogTemperatureSensor, BoilerAdapter, BoilerCascade, Command, DayTimetable, HeatingCircuit, IntervalTimetable, IOExtension, ModBusSignal, RadioSensor, TimeInterval, WeekTimetable, WiredTemperatureSensor, } from 'z3k-config';
import { getDeviceSpec } from 'z3k-parser/model/device-spec/z3k-feature-flags';
import { getIOExtensionSpec } from 'z3k-parser/model/device-spec/io-extensions-spec';
import { removeNulls, selectStripItemsOfArray, } from 'utils';
import { Range } from 'immutable';
import { boilersOptionsSelector } from 'z3k-settings/components/utils/z3k-selectors';
import { composePhysicalIO } from 'z3k-parser/lib/input-outputs/utils';
import { getWiredTemperatureSensor } from 'z3k-parser/lib/sensors/wired';
import { HeatingMode, } from 'z3k-parser/model/z3k-config/20-heating-mode';
import { getRadioThermometers, idExtractor, objects2ids } from 'z3k-parser/lib/index';
const empty_array = [];
function select_object_array_parts(array_name, fields) {
    return createSelector((config) => (config[array_name] ?? empty_array), selectStripItemsOfArray(fields));
}
export const analog_input_options = createSelector(select_object_array_parts('analog_inputs', [
    'id',
    'name',
]), (analog_inputs) => [{ name: 'Датчики', options: analog_inputs }]);
export const analog_output_options = createSelector(select_object_array_parts('analog_outputs', [
    'id',
    'name',
]), (analog_outputs) => analog_outputs.length > 0 ? [{ name: 'Аналоговые', options: analog_outputs }] : []);
export const analogOutputs = createSelector(select_object_array_parts('analog_outputs', [
    'id',
    'name',
]), (analog_outputs) => ({ name: 'Аналоговые выходы', options: analog_outputs }));
export const analog_output_physical_io_options = createSelector(select_object_array_parts('analog_outputs', [
    'id',
    'name',
]), (analog_outputs) => {
    const options = analog_outputs.map(({ id, name }) => ({
        id: composePhysicalIO(id, 0),
        name,
    }));
    return options.length > 0 ? [{ name: 'Аналоговые', options }] : [];
});
export const convertOptions2ObjectLink = ({ text, value, }) => ({ name: text, id: value });
export function modBusForWiredTempSensors(modbus_devices, modbus_signals) {
    return modBusSignalOptions(modbus_devices ?? [], modbus_signals ?? [], (signal) => ModBusSignal.WiredTemperatureSensorTypes.includes(signal.type));
}
export function modBusForOutputActions(modbus_devices, modbus_signals) {
    return modBusSignalOptions(modbus_devices ?? [], modbus_signals ?? [], (signal) => ModBusSignal.OutputActionTypes.includes(signal.type));
}
export function modBusForAnalogInputs(modbus_devices, modbus_signals) {
    return modBusSignalOptions(modbus_devices ?? [], modbus_signals ?? [], (signal) => ModBusSignal.AnalogInputTypes.includes(signal.type));
}
export function modBusForAnalogOutputs(modbus_devices, modbus_signals) {
    return modBusSignalOptions(modbus_devices ?? [], modbus_signals ?? [], (signal) => ModBusSignal.AnalogOutputTypes.includes(signal.type));
}
export const modBusSignalOptions = (modbus_devices, modbus_signals, filter_signals) => {
    return modbus_devices
        .map((dev) => ModBusSignal.getSignalsByDevice(modbus_signals, dev)
        .filter(filter_signals)
        .map((signal) => Range(0, signal.var_count)
        .map((i) => ({
        text: `${dev.name} / ${signal.name}` +
            (signal.var_count > 1 ? ` №${i + 1}` : ''),
        value: (signal.id << 4) | i,
    }))
        .toArray()))
        .flat(2);
};
export function getDeviceWiredTemperatureSensorOptions(device_spec, modbus_devices, modbus_signals) {
    let modbus_options = [];
    if (device_spec.io.has_modbus) {
        modbus_options = modBusForWiredTempSensors(modbus_devices, modbus_signals);
    }
    return [
        { text: 'Не выбрано', value: -1 },
        ...modbus_options,
    ];
}
export function hasNonEmptyOptions(options) {
    return options.filter((option) => option.value != -1).length > 0;
}
export function getDeviceInputsOptions(serial, device_spec, io_extensions, io_radio_relays, includeNTC, includeVoltages, modbus_devices, modbus_signals) {
    const voltage_inputs = [];
    if (includeVoltages) {
        if (device_spec.io.power_voltage_input != null)
            voltage_inputs.push({
                is_voltage_control: 1,
                text: 'Напряжение питания',
                value: device_spec.io.power_voltage_input,
            });
        if (device_spec.io.battery_voltage_input != null)
            voltage_inputs.push({
                is_voltage_control: 1,
                text: 'Напряжение батареи',
                value: device_spec.io.battery_voltage_input,
            });
    }
    let io_options = [];
    io_options = [
        ...IOExtension.filterIOExtensions(device_spec, io_extensions, serial),
        ...(io_radio_relays != null ? io_radio_relays : []),
    ].flatMap((io_ext) => {
        const spec = getIOExtensionSpec(io_ext.type, io_ext.serial, device_spec);
        if (!device_spec.io.io_extensions_supported && !spec.always_accessible) {
            return [];
        }
        const voltage_inputs = [];
        if (includeVoltages) {
            if (spec.power_voltage_input !== null)
                voltage_inputs.push({
                    is_voltage_control: 1,
                    text: spec.show_name
                        ? `${io_ext.name}: Напряжение питания`
                        : 'Напряжение питания',
                    value: composePhysicalIO(io_ext.id, spec.power_voltage_input),
                });
            if (spec.battery_voltage_input !== null)
                voltage_inputs.push({
                    is_voltage_control: 1,
                    text: spec.show_name
                        ? `${io_ext.name}: Напряжение батареи`
                        : 'Напряжение батареи',
                    value: composePhysicalIO(io_ext.id, spec.battery_voltage_input),
                });
        }
        return [
            ...voltage_inputs,
            ...spec.inputs.map((input) => ({
                text: spec.show_name ? `${io_ext.name}: ${input.text}` : input.text,
                value: composePhysicalIO(io_ext.id, input.value),
            })),
            ...(includeNTC ? spec.ntc_inputs : []).map((input) => ({
                text: spec.show_name ? `${io_ext.name}: ${input.text}` : input.text,
                value: composePhysicalIO(io_ext.id, input.value),
            })),
        ];
    });
    let modbus_options = [];
    if (device_spec.io.has_modbus && modbus_devices != null && modbus_signals != null) {
        modbus_options = modBusForAnalogInputs(modbus_devices, modbus_signals);
    }
    return [
        { text: 'Не выбрано', value: -1 },
        ...modbus_options,
        ...device_spec.io.inputs,
        ...(includeNTC ? device_spec.io.ntc_inputs : []),
        ...voltage_inputs,
        ...io_options,
    ];
}
export function getDeviceOutputsOptions(serial, device_spec, io_extensions, io_radio_relays, modbus_devices = [], modbus_signals = [], analog_outputs = []) {
    const io_ext_and_radio_relays = [
        ...IOExtension.filterIOExtensions(device_spec, io_extensions, serial),
        ...(io_radio_relays != null ? io_radio_relays : []),
    ];
    const io_options = io_ext_and_radio_relays.flatMap((io_ext) => {
        const spec = getIOExtensionSpec(io_ext.type, io_ext.serial, device_spec);
        if (!device_spec.io.io_extensions_supported && !spec.always_accessible) {
            return [];
        }
        return [
            ...spec.relay_outputs.map((relay) => ({
                text: spec.show_name ? `${io_ext.name}: ${relay.text}` : relay.text,
                value: composePhysicalIO(io_ext.id, relay.value),
            })),
            ...spec.open_collector_outputs.map((oc) => ({
                text: spec.show_name ? `${io_ext.name}: ${oc.text}` : oc.text,
                value: composePhysicalIO(io_ext.id, oc.value),
            })),
        ];
    });
    let modbus_options = [];
    if (device_spec.io.has_modbus) {
        modbus_options = modBusForOutputActions(modbus_devices, modbus_signals);
    }
    const analog_options = (analog_outputs ?? []).map((output) => ({
        text: output.name,
        value: composePhysicalIO(output.id, 0),
    }));
    return [
        { text: 'Не выбрано', value: -1 },
        ...modbus_options,
        ...device_spec.io.relay_outputs,
        ...device_spec.io.open_collector_outputs,
        ...io_options,
        ...analog_options,
    ];
}
const analogTemperatureSensorTypes = [
    [AnalogTemperatureSensor.Type.Pt100, 'Pt100'],
    [AnalogTemperatureSensor.Type.Pt500, 'Pt500'],
    [AnalogTemperatureSensor.Type.Pt1000, 'Pt1000'],
    [AnalogTemperatureSensor.Type.NTC1, 'NTC1'],
    [AnalogTemperatureSensor.Type.NTC1_8, 'NTC1.8'],
    [AnalogTemperatureSensor.Type.NTC2, 'NTC2'],
    [AnalogTemperatureSensor.Type.NTC3, 'NTC3'],
    [AnalogTemperatureSensor.Type.NTC5, 'NTC5'],
    [AnalogTemperatureSensor.Type.NTC10, 'NTC10'],
    [AnalogTemperatureSensor.Type.NTC20, 'NTC20'],
    [AnalogTemperatureSensor.Type.NTC47, 'NTC47'],
];
export function getOptionsForAnalogTemperatureSensorType() {
    return [
        { text: 'Не выбрано', value: -1 },
        ...analogTemperatureSensorTypes.map(([type, name]) => ({
            text: name,
            value: type,
        })),
        { text: 'Другой', value: AnalogTemperatureSensor.Type.OTHER },
    ];
}
export const device_output_options = createSelector((config) => config.login, (config) => getDeviceSpec(config.device.name, config.device.hardware), select_object_array_parts('io_extensions', ['id', 'name', 'type', 'serial']), select_object_array_parts('radiosensors', ['id', 'name', 'type', 'serial']), select_object_array_parts('modbus_devices', ['id', 'name', 'signals']), select_object_array_parts('modbus_signals', ['id', 'name', 'type', 'var_count']), select_object_array_parts('analog_outputs', [
    'id',
    'name',
]), (login, device_spec, io_extensions, radiosensors, modbus_devices, modbus_signals, analog_outputs) => {
    const io_radio_relays = radiosensors.filter(({ type }) => type === RadioSensor.Type.RADIO_RELAY);
    const io_ext_and_radio_relays = [
        ...IOExtension.filterIOExtensions(device_spec, io_extensions, login),
        ...io_radio_relays,
    ];
    const relay_options = removeNulls(io_ext_and_radio_relays.flatMap((io_ext) => {
        const spec = getIOExtensionSpec(io_ext.type, io_ext.serial, device_spec);
        if (!device_spec.io.io_extensions_supported &&
            !spec.always_accessible) {
            return null;
        }
        return spec.relay_outputs.map((relay) => ({
            name: spec.show_name ? `${io_ext.name}: ${relay.text}` : relay.text,
            id: composePhysicalIO(io_ext.id, relay.value),
        }));
    }));
    const ok_options = removeNulls(io_ext_and_radio_relays.flatMap((io_ext) => {
        const spec = getIOExtensionSpec(io_ext.type, io_ext.serial, device_spec);
        if (!device_spec.io.io_extensions_supported &&
            !spec.always_accessible) {
            return null;
        }
        return spec.open_collector_outputs.map((oc) => ({
            name: spec.show_name ? `${io_ext.name}: ${oc.text}` : oc.text,
            id: composePhysicalIO(io_ext.id, oc.value),
        }));
    }));
    const relay_outputs = [
        ...device_spec.io.relay_outputs.map(convertOptions2ObjectLink),
        ...relay_options,
    ];
    const ok_outputs = [
        ...device_spec.io.open_collector_outputs.map(convertOptions2ObjectLink),
        ...ok_options,
    ];
    const analog_options = analog_outputs.map((output) => ({
        name: output.name,
        id: composePhysicalIO(output.id, 0),
    }));
    const modbus_outputs = device_spec.io.has_modbus
        ? modBusForOutputActions(modbus_devices, modbus_signals).map(convertOptions2ObjectLink)
        : [];
    return removeNulls([
        relay_outputs.length > 0
            ? {
                name: 'Релейные',
                options: relay_outputs,
            }
            : null,
        ok_outputs.length > 0
            ? {
                name: 'ОК',
                options: ok_outputs,
            }
            : null,
        analog_options.length > 0
            ? {
                name: 'Аналоговые',
                options: analog_options,
            }
            : null,
        modbus_outputs.length > 0
            ? {
                name: 'Modbus',
                options: modbus_outputs,
            }
            : null,
    ]);
});
export const analog_input_pressure_options = createSelector(select_object_array_parts('analog_inputs', ['id', 'name', 'sensor_type']), (analog_inputs) => {
    const options = analog_inputs.filter((ai) => AnalogInput.isPressureInput(ai) || AnalogInput.isAnalogInputType(ai));
    return options.length > 0 ? [{ name: 'Датчики', options }] : [];
});
export const select_guard_zone_and_output_action_options = createSelector(select_object_array_parts('output_actions', [
    'id',
    'name',
]), Command.get.all, select_object_array_parts('guard_zones', ['id', 'name']), (output_actions, commands, guard_zones) => {
    return [
        {
            name: 'Охранные зоны',
            options: guard_zones.flatMap((zone) => {
                const cmds = commands.filter((cmd) => cmd.objectid == zone.id && cmd.cmd == '2');
                if (cmds.length == 0)
                    return [
                        {
                            ...zone,
                            name: `Управлять охранной зоной «${zone.name}»`,
                        },
                    ];
                return cmds;
            }),
        },
        {
            name: 'Действия с выходами',
            options: output_actions,
        },
    ];
});
export const boilerCircuitBoilerGroupOptionsSelector = createSelector(boilersOptionsSelector, select_object_array_parts('boiler_groups', [
    'id',
    'name',
]), select_object_array_parts('boiler_cascades', [
    'id',
    'name',
]), (boiler_options, boiler_groups, boiler_cascades) => [
    ...boiler_options,
    { name: 'Группы контуров котлов', options: boiler_groups },
    { name: 'Каскады контуров котлов', options: boiler_cascades },
]);
export const boilerAdaptersSelector = idExtractor([BoilerAdapter]);
export const getDHWOptions = createSelector(select_object_array_parts('heating_circuits', ['id', 'name', 'type']), (hcs) => [
    { text: 'Не выключать', value: 0 },
    { text: 'При любом', value: -1 },
    ...hcs
        .filter((circ) => circ.type === HeatingCircuit.Type.DHW)
        .map((circ) => ({
        text: circ.name,
        value: circ.id,
    })),
]);
export const selectAnalogInputsOptions = createSelector(AnalogInput.get.all, (inputs) => [
    {
        name: 'Аналоговые входы',
        options: inputs,
    },
]);
export const consumer_heating_circuits_options_selector = createSelector(select_object_array_parts('heating_circuits', ['id', 'name', 'type']), (heating_circuits) => [
    {
        name: 'Отопительные контуры и ГВС',
        options: heating_circuits.filter((hc) => !HeatingCircuit.isBoiler(hc)),
    },
]);
export const boiler_cascades_and_hc_options_selector = createSelector(select_object_array_parts('heating_circuits', ['id', 'name', 'type']), select_object_array_parts('boiler_cascades', [
    'id',
    'name',
]), (heating_circuits, boiler_cascades) => [
    {
        name: 'Котловые контуры отопления',
        options: heating_circuits.filter((hc) => HeatingCircuit.isBoiler(hc)),
    },
    {
        name: 'Каскады котлов',
        options: boiler_cascades,
    },
]);
export const heating_modes_options_selector = createSelector(select_object_array_parts('heating_modes', ['id', 'name', 'setting_register', 'heating_zones']), (_, hm_id) => hm_id, (heating_modes, hm_id) => [
    {
        name: 'Режимы отопления',
        options: heating_modes.filter((hm) => hm.id !== hm_id),
    },
]);
export const wired_radio_options_selector = createSelector(select_object_array_parts('wired_temperature_sensors', ['id', 'name']), select_object_array_parts('analog_temperature_sensors', ['id', 'name']), select_object_array_parts('radiosensors', [
    'id',
    'name',
    'type',
]), (wired_sensors, analog_sensor, radio_sensor) => {
    const radio_thermometer = radio_sensor.filter(RadioSensor.isThermometer);
    return removeNulls([
        wired_sensors.length > 0
            ? {
                name: 'Цифровые',
                options: wired_sensors,
            }
            : null,
        analog_sensor.length > 0
            ? {
                name: 'Аналоговые',
                options: analog_sensor,
            }
            : null,
        radio_thermometer.length > 0
            ? {
                name: 'Радиодатчики',
                options: radio_thermometer,
            }
            : null,
    ]);
});
export const wired_radio_boiler_adapter_options_selector = createSelector(wired_radio_options_selector, select_object_array_parts('boiler_adapters', [
    'id',
    'name',
]), (wired_radio, boiler_adapters) => {
    return removeNulls([
        ...wired_radio,
        boiler_adapters.length > 0
            ? {
                name: 'Адаптеры котлов',
                options: boiler_adapters,
            }
            : null,
    ]);
});
export const circuit_type_options_selector = createSelector((config) => getDeviceSpec(config.device.name, config.device.hardware), (spec) => {
    const withCooling = spec.heating.has_heating &&
        spec.heating.has_extended_heating.show_cooling_circuits;
    return removeNulls([
        {
            text: 'Контур котла',
            value: HeatingCircuit.Type.BOILER,
        },
        {
            text: 'Контур ГВС',
            value: HeatingCircuit.Type.DHW,
        },
        withCooling
            ? {
                text: 'Контур охлаждения',
                value: HeatingCircuit.Type.COOLING,
            }
            : null,
        {
            text: 'Контур потребителя',
            value: HeatingCircuit.Type.HEATING,
        },
    ]);
});
export const hasBoilerMode = createSelector((_, hm) => hm, HeatingCircuit.get.all, BoilerCascade.get.all, HeatingMode.hasBoilerMode);
export const isReserve = (temp_settings, day_timetables, week_timetables, interval_timetables, time_intervals) => {
    if (temp_settings === HeatingMode.RESERVE_MODE) {
        return true;
    }
    if (day_timetables.some(({ id, temp24 }) => id === temp_settings && temp24.includes(HeatingMode.RESERVE_MODE))) {
        return true;
    }
    // prettier-ignore
    const current_week = week_timetables
        .find(({ id }) => id === temp_settings);
    if (current_week != null) {
        return day_timetables
            .filter(({ id }) => current_week.temp7.includes(id))
            .some(({ temp24 }) => temp24.includes(HeatingMode.RESERVE_MODE));
    }
    // prettier-ignore
    const current_timetable = interval_timetables
        .find(({ id }) => id === temp_settings);
    if (current_timetable != null) {
        if (current_timetable.outside_default === HeatingMode.RESERVE_MODE) {
            return true;
        }
        return time_intervals
            .filter(({ id }) => current_timetable.time_intervals.includes(id))
            .some(({ temperature }) => temperature === HeatingMode.RESERVE_MODE);
    }
    return false;
};
export const isBoilerReserve = createSelector((_, temp_settings_id) => temp_settings_id, DayTimetable.get.all, WeekTimetable.get.all, IntervalTimetable.get.all, TimeInterval.get.all, isReserve);
export const physical_and_modbus_analog_output_options = createSelector((config) => getDeviceSpec(config.device.name, config.device.hardware), select_object_array_parts('modbus_devices', ['id', 'name', 'signals']), select_object_array_parts('modbus_signals', ['id', 'name', 'type', 'var_count']), select_object_array_parts('io_extensions', ['id', 'name', 'type', 'serial']), (device_spec, modbus_devices, modbus_signals, io_extensions) => {
    const modbus_outputs = modBusForAnalogOutputs(modbus_devices, modbus_signals).map(convertOptions2ObjectLink);
    const builtin_analog_outputs = removeNulls(io_extensions.flatMap((io_ext) => {
        const spec = getIOExtensionSpec(io_ext.type, io_ext.serial, device_spec);
        if (!device_spec.io.io_extensions_supported &&
            !spec.always_accessible) {
            return null;
        }
        return spec.analog_outputs.map((ao) => ({
            name: spec.show_name ? `${io_ext.name}: ${ao.text}` : ao.text,
            id: composePhysicalIO(io_ext.id, ao.value),
        }));
    }));
    return removeNulls([
        builtin_analog_outputs.length > 0
            ? {
                name: 'Встроенные аналоговые выходы',
                options: builtin_analog_outputs,
            }
            : null,
        modbus_outputs.length > 0
            ? {
                name: 'Modbus',
                options: modbus_outputs,
            }
            : null,
    ]);
});
export const get_temperature_sensors = createSelector(getWiredTemperatureSensor, AnalogTemperatureSensor.get.all, getRadioThermometers, (wts, ats, rs) => {
    return [...wts, ...ats, ...rs];
});
export const get_temperature_sensors_plus_adapters = createSelector(getWiredTemperatureSensor, AnalogTemperatureSensor.get.all, getRadioThermometers, BoilerAdapter.get.all, (wts, ats, rs, ba) => {
    return [...wts, ...ats, ...rs, ...ba];
});
export const get_all_temperature_sensors_plus_adapters = createSelector(WiredTemperatureSensor.get.all, AnalogTemperatureSensor.get.all, RadioSensor.get.all, BoilerAdapter.get.all, (wts, ats, rs, ba) => {
    return removeNulls([
        wts.length > 0
            ? {
                name: 'Цифровые термодатчики',
                group: 'wired_temperature_sensors',
                field: 'setting_register',
                options: wts,
            }
            : null,
        ats.length > 0
            ? {
                name: 'Аналоговые термодатчики',
                group: 'analog_temperature_sensors',
                field: 'setting_register',
                options: ats,
            }
            : null,
        rs.length > 0
            ? {
                name: 'Радиодатчики',
                group: 'radiosensors',
                field: 'setting_register',
                options: rs,
            }
            : null,
        ba.length > 0
            ? {
                name: 'Адаптеры котлов',
                group: 'boiler_adapters',
                field: 'logged_parameter_register',
                options: ba,
            }
            : null,
    ]);
});
export const get_temperature_sensors_plus_adapters_ids = createSelector(get_temperature_sensors_plus_adapters, objects2ids);
export const select_visible_heating_modes = (z3k_config) => HeatingMode.get.where(z3k_config, (hm) => !HeatingMode.isHidden(hm));
export const select_consumer_modes = createSelector(BoilerCascade.get.all, HeatingCircuit.get.all, select_visible_heating_modes, (cascades, heating_circuits, heating_modes) => heating_modes.filter((hm) => HeatingMode.filterConsumerMode(hm, heating_circuits, cascades)));
export const select_boiler_modes = createSelector(BoilerCascade.get.all, HeatingCircuit.get.all, select_visible_heating_modes, (cascades, heating_circuits, heating_modes) => heating_modes.filter((hm) => HeatingMode.filterBoilerMode(hm, heating_circuits, cascades)));
